<template>
  <div class="ps-panel">
    <div class="ps-panel-hd">
      <h2>E-mail</h2>
      <p>Vincule um endereço de e-mail para recuperação de acesso.</p>
    </div>
    <div class="ps-email-block">
      <div v-if="currentEmail && !changingEmail" class="ps-email-current">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" style="opacity:.5"><rect x="2" y="4" width="12" height="8" rx="1.5"/><path d="M2 5.5l6 4 6-4" stroke-linecap="round"/></svg>
        <span class="ps-email-val">{{ currentEmail }}</span>
        <span class="email-badge">{{ $t('common.verified') }}</span>
        <button class="link-btn" type="button" @click="startChangeEmail">{{ $t('profile.emailChange') }}</button>
      </div>
      <template v-else-if="emailStep === 1">
        <div class="email-input-row">
          <input v-model="pendingEmail" type="email" placeholder="seu@email.com" class="email-input" :disabled="emailLoading" />
          <button class="btn btn-ghost btn-login" type="button" :disabled="emailLoading || !pendingEmail.trim()" @click="sendVerificationCode">
            {{ emailLoading ? $t('profile.emailSending') : $t('profile.emailSendCode') }}
          </button>
        </div>
        <p v-if="emailError" class="email-error">{{ emailError }}</p>
        <button v-if="changingEmail" class="link-btn" type="button" style="margin-top:4px" @click="cancelChangeEmail">{{ $t('profile.emailCancelChange') }}</button>
      </template>
      <template v-else-if="emailStep === 2">
        <p class="email-hint">{{ $t('profile.emailHint', { email: pendingEmail }) }}</p>
        <div class="email-input-row">
          <input v-model="verificationCode" type="text" placeholder="000000" maxlength="6" class="email-input otp-input" :disabled="emailLoading" inputmode="numeric" />
          <button class="btn btn-ghost btn-login" type="button" :disabled="emailLoading || verificationCode.length < 6" @click="confirmCode">
            {{ emailLoading ? $t('profile.emailVerifying') : $t('profile.emailConfirm') }}
          </button>
        </div>
        <p v-if="emailError" class="email-error">{{ emailError }}</p>
        <button class="link-btn" type="button" style="margin-top:4px" @click="emailStep = 1">{{ $t('profile.emailResend') }}</button>
      </template>
      <div v-else-if="emailStep === 3" class="email-success">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1f8a45" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        {{ $t('profile.emailSuccess', { email: currentEmail }) }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { apiFetch } from '@/store/api'
import { accountsStore } from '@/store/accounts'
import { sessionStore } from '@/store/session'

export default defineComponent({
  name: 'ProfileEmailTab',
  setup() {
    const { t } = useI18n()
    const currentEmail = ref('')
    const changingEmail = ref(false)
    const emailStep = ref<1 | 2 | 3>(1)
    const pendingEmail = ref('')
    const verificationCode = ref('')
    const emailLoading = ref(false)
    const emailError = ref('')

    onMounted(async () => {
      const session = sessionStore.state.user
      if (!session) return
      await accountsStore.refresh().catch(() => { /* ignore */ })
      const account = accountsStore.all().find(a => a.uid === session.uid || a.username === session.username)
      if (account) currentEmail.value = account.email || ''
    })

    const startChangeEmail = () => {
      pendingEmail.value = currentEmail.value
      emailStep.value = 1
      emailError.value = ''
      verificationCode.value = ''
      changingEmail.value = true
    }

    const cancelChangeEmail = () => {
      changingEmail.value = false
      emailStep.value = 1
      pendingEmail.value = ''
      verificationCode.value = ''
      emailError.value = ''
    }

    const sendVerificationCode = async () => {
      emailError.value = ''
      const em = pendingEmail.value.trim()
      if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
        emailError.value = t('profile.emailInvalidError')
        return
      }
      emailLoading.value = true
      try {
        await apiFetch('/request-email-verification', { method: 'POST', body: JSON.stringify({ email: em }) })
        verificationCode.value = ''
        emailStep.value = 2
      } catch (err: unknown) {
        emailError.value = err instanceof Error ? err.message : t('profile.emailGenericError')
      } finally {
        emailLoading.value = false
      }
    }

    const confirmCode = async () => {
      emailError.value = ''
      emailLoading.value = true
      try {
        const result = await apiFetch<{ ok: boolean; email: string }>('/confirm-email-verification', {
          method: 'POST',
          body: JSON.stringify({ code: verificationCode.value.trim() })
        })
        currentEmail.value = result.email
        changingEmail.value = false
        emailStep.value = 3
        verificationCode.value = ''
        pendingEmail.value = ''
      } catch (err: unknown) {
        emailError.value = err instanceof Error ? err.message : t('profile.emailInvalidCode')
      } finally {
        emailLoading.value = false
      }
    }

    return {
      currentEmail, changingEmail, emailStep,
      pendingEmail, verificationCode, emailLoading, emailError,
      startChangeEmail, cancelChangeEmail, sendVerificationCode, confirmCode,
    }
  }
})
</script>

<style scoped lang="scss">
.ps-panel-hd { margin-bottom: 24px; h2 { font-size: 18px; font-weight: 700; color: var(--ink); margin: 0 0 4px; } p { font-size: 13px; color: var(--ink-muted); margin: 0; } }

.ps-email-block {
  background: var(--card, #fff); border: 1px solid var(--border, rgba(31,30,26,.1));
  border-radius: 12px; padding: 20px; display: flex; flex-direction: column; gap: 12px;
}

.ps-email-current { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.ps-email-val { font-size: 14px; font-weight: 500; color: var(--ink); }

.email-badge {
  font-size: 11px; font-weight: 700; color: #1f8a45; background: #e8f5ee;
  border-radius: 20px; padding: 2px 8px; text-transform: uppercase; letter-spacing: .04em;
}

.email-input-row { display: flex; gap: 8px; align-items: center; }
.email-input {
  flex: 1; min-width: 0; padding: 9px 12px; border: 1.5px solid rgba(31,30,26,.2);
  border-radius: 8px; font-size: 14px; outline: none; transition: border-color .15s;
  background: var(--surface, #fff); color: var(--ink, #2c2b27);
  &:focus { border-color: var(--accent, #2f6fce); }
  &:disabled { opacity: .55; }
}
.otp-input { max-width: 120px; text-align: center; font-size: 20px; font-weight: 700; letter-spacing: 6px; }

.email-hint { margin: 0; font-size: 13px; color: var(--ink-muted); line-height: 1.5; }
.email-error { margin: 0; font-size: 13px; color: #a54b2a; }
.email-success { display: flex; align-items: center; gap: 7px; font-size: 13px; color: #1f8a45; }

.link-btn {
  background: transparent; border: none; cursor: pointer; font-size: 13px;
  color: var(--accent, #2f6fce); padding: 0; text-decoration: underline; text-underline-offset: 2px;
  &:hover { opacity: .8; }
}

.btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; border: 1px solid transparent; border-radius: var(--radius, 6px); padding: 6px 16px; font-size: 14px; font-weight: 600; cursor: pointer; transition: background .12s; }
.btn-ghost { background: transparent; color: var(--ink); border-color: var(--border-strong); &:hover { background: var(--surface-3); } }
.btn-login { padding: 10px 22px; border-radius: 8px; }
</style>
